import "../styles/Home.css";
import Service from "../components/Service";
import { useEffect } from "react";
import Projects from "../components/Projects";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";

function Home() {
  const infoItems = [
    { title: "Year Established", value: "1974" },
    { title: "Projects Completed", value: "210" },
    { title: "Current Projects", value: "26" },
    { title: "Employees", value: "182" },
  ];

  const imagePaths = [
    "../assets/clients/coj.png",
    "../assets/clients/cosa.png",
    "../assets/clients/cc.png",
    "../assets/clients/ccu.png",
    "../assets/clients/alsop.png",
    "../assets/clients/cornerlot.png",
    "../assets/clients/drhorton.png",
    "../assets/clients/greenpointe.png",
    "../assets/clients/stjoe.png",
    "../assets/clients/tollbros.png",
  ];

  // MARK: Info container count up animation
  useEffect(() => {
    const countUp = (element) => {
      const target = +element.getAttribute("data-target");
      const duration = 2000;
      const increment = target / (duration / 16);

      let current = 0;

      const updateCount = () => {
        current += increment;
        if (current < target) {
          element.textContent = Math.ceil(current);
          requestAnimationFrame(updateCount);
        } else {
          element.textContent = target;
        }
      };

      updateCount();
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          !entry.target.hasAttribute("data-animated")
        ) {
          countUp(entry.target);
          entry.target.setAttribute("data-animated", true); // Mark as animated
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.5,
    });

    const elements = document.querySelectorAll(".info-item h2");
    elements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      elements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  // MARK: Scroll to the anchors
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 200); // Adjust the timeout if necessary
      }
    }
  }, [location]);

  return (
    <>
      <Helmet>
        <title>
          Jax Utilities Management | Site Development & Underground Utilities
        </title>
        <meta
          name="description"
          content="Jaxum specializes in site development, underground utilities, water & sewer systems in Jacksonville, FL. Quality services since 1974."
        />
        <meta
          name="keywords"
          content="site development, utilities, underground utilities, water systems, sewer systems, Jacksonville, Florida, construction"
        />
        <meta
          property="og:title"
          content="Jax Utilities Management | Site Development & Underground Utilities"
        />
        <meta
          property="og:description"
          content="Leading site development company in Jacksonville specializing in underground utilities, water & sewer systems. Quality services since 1974."
        />
        <meta
          property="og:image"
          content="https://jum-email-signature.s3.us-east-2.amazonaws.com/JAXUM+Logo+(bold+est).png"
        />
        <meta property="og:url" content="https://www.jaxum.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://jum-email-signature.s3.us-east-2.amazonaws.com/JAXUM+Logo+(bold+est).png"
        />
        <link rel="" href="https://www.jaxum.com/" />
      </Helmet>

      <div className="home-container">
        <div className="home-content">
          <div className="header-container">
            <div className="header">
              <video id="header-video" autoPlay loop muted playsInline>
                <source
                  src="../assets/video/header-video.mp4"
                  type="video/mp4"
                />
              </video>

              <div className="header-overlay">
                <h1 className="header-title">
                  <span>
                    COMMITTED TO PROVIDING QUALITY SERVICES & WORKMANSHIP AT THE
                    MOST REASONABLE COST POSSIBLE.
                  </span>
                </h1>
              </div>
            </div>
          </div>

          <Service />

          <div className="info-container">
            <div className="info-content">
              <div className="info">
                {infoItems.map((item, index) => (
                  <div key={index} className="info-item">
                    <h2 id={`count-up-${index}`} data-target={item.value}>
                      0
                    </h2>
                    <h4>{item.title}</h4>
                    <span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div id="about-us" className="about-container">
            <div className="about-content">
              <div className="about">
                <div className="about-header">
                  <h3 className="about-title">WHO WE ARE</h3>
                </div>
                <div className="about-body-right">
                  <p>
                    Jax Utilities Management, Inc. (JAXUM) was established in
                    1974 as a utility management company that had full
                    responsibility for installation, maintenance, and operations
                    of various water and wastewater systems in NE Florida,
                    including the City of Jacksonville. Since the founding of
                    JAXUM, we have grown to become a leading site development
                    company specializing in the building, reconstruction and
                    maintenance of roads, water, wastewater, and storm drain
                    systems.Our spectrum of services also include clearing,
                    earthwork, utility problem investigation, emergency storm
                    drain cave-in repairs, pump station installation and
                    repairs, urethane injection systems, and concrete repairs.
                    Over the years, JUM has experienced steady growth, through
                    successful completion of projects and through building solid
                    relationships with industry-leading companies and land
                    development firms. The focus of offering quality, consistent
                    service at fair pricing has remained central to the mission
                    and success of JUM.
                  </p>
                </div>
              </div>
              <div className="about-image">
                <img src="../assets/logo.svg" alt="About Us" />
              </div>
              <div className="about">
                <div className="about-header">
                  <h2 className="about-title">OUR VISION</h2>
                </div>
                <div className="about-body-left">
                  <p>
                    Jax Utilities Management is committed to providing quality
                    services and workmanship at the most reasonable costs
                    possible.  This commitment to excellence sets JUM apart from
                    many other organizations in the Underground Utilities
                    industry.  JUM, as a company, is rich in history and has
                    served as a leader in the Greater Jacksonville area for over
                    50 years. We have been successful in blending a lifetime of
                    experience, a highly trained and motivated workforce, and an
                    innovative approach to create maximum value for our clients.
                     JUM maintains the highest standard of integrity, quality,
                    productivity, safety and teamwork while servings our
                    clients’ site development needs. We have a focused and
                    structured process that allows us to be proficient, but
                    flexible.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <Projects />

          <div className="client-container">
            <div className="client-content">
              <p className="client-title">OUR CLIENTS</p>
              <div className="scroller">
                <div className="scroller__inner">
                  {imagePaths.map((img, index) => (
                    <img src={img} alt={index} key={index} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
